import { useState } from "react";
import ReactDiffViewer, { DiffMethod } from "react-diff-viewer-continued";
import { Box } from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import { AcxMessageBox } from "components/UI/AcxMessageBox";
import AcxToggleButton from "components/UI/AcxToggleButton";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import * as AYSDialog from "components/UI/AYSDialog";
import type { DataSourceType } from "./types";

const DIFF_VIEW_STORAGE_KEY = "datasource-diff-view";
const DIFF_WRAP_STORAGE_KEY = "datasource-diff-wrap";
type DiffView = "unified" | "split";
type WrapMode = "wrap" | "nowrap";

const readDiffViewPreference = (): DiffView =>
    localStorage.getItem(DIFF_VIEW_STORAGE_KEY) === "split"
        ? "split"
        : "unified";

const readWrapPreference = (): WrapMode =>
    localStorage.getItem(DIFF_WRAP_STORAGE_KEY) === "nowrap"
        ? "nowrap"
        : "wrap";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    isLoading: boolean;
    before: DataSourceType;
    after: DataSourceType;
};

const SaveConfigurationConfirmationDialog = ({
    isOpen,
    onClose,
    onConfirm,
    isLoading,
    before,
    after,
}: Props) => {
    const [diffView, setDiffView] = useState<DiffView>(readDiffViewPreference);
    const [wrapMode, setWrapMode] = useState<WrapMode>(readWrapPreference);
    const oldJson = JSON.stringify(before, null, 2);
    const newJson = JSON.stringify(after, null, 2);

    const handleViewChange = (value: DiffView | null) => {
        if (!value) return;
        setDiffView(value);
        localStorage.setItem(DIFF_VIEW_STORAGE_KEY, value);
    };

    const handleWrapChange = (value: WrapMode | null) => {
        if (!value) return;
        setWrapMode(value);
        localStorage.setItem(DIFF_WRAP_STORAGE_KEY, value);
    };

    return (
        <AYSDialog.Container
            isOpen={isOpen}
            onClose={onClose}
            icon={<InfoRoundedIcon color="primary" sx={{ fontSize: 32 }} />}
            maxWidth={diffView === "split" ? "lg" : "md"}
        >
            <AYSDialog.Title>Confirm changes to Data Sources</AYSDialog.Title>
            <AYSDialog.Subtitle>
                You are about to update this Data Source configuration. Review
                the changes below carefully before confirming. This cannot be
                undone.
            </AYSDialog.Subtitle>
            <AYSDialog.Content
                sx={{ padding: "0 !important", background: "none" }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1,
                        }}
                    >
                        <AcxToggleButton
                            id="diff-wrap-toggle"
                            exclusive
                            defaultValue={wrapMode}
                            onChange={handleWrapChange}
                            items={[
                                { value: "wrap", element: "Wrap" },
                                { value: "nowrap", element: "No wrap" },
                            ]}
                        />
                        <AcxToggleButton
                            id="diff-view-toggle"
                            exclusive
                            defaultValue={diffView}
                            onChange={handleViewChange}
                            items={[
                                { value: "unified", element: "Unified" },
                                { value: "split", element: "Split" },
                            ]}
                        />
                    </Box>
                    <Box
                        sx={{
                            maxHeight: 400,
                            overflowY: "auto",
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            fontSize: 12,
                        }}
                    >
                        <ReactDiffViewer
                            oldValue={oldJson}
                            newValue={newJson}
                            splitView={diffView === "split"}
                            compareMethod={DiffMethod.WORDS}
                            useDarkTheme={false}
                            hideLineNumbers={false}
                            showDiffOnly
                            styles={{
                                diffContainer: {
                                    minWidth: "unset",
                                    overflowX:
                                        wrapMode === "nowrap"
                                            ? "auto"
                                            : "unset",
                                },
                                line: { fontFamily: "monospace", fontSize: 12 },
                                content: { fontSize: 12 },
                                contentText:
                                    wrapMode === "nowrap"
                                        ? {
                                              whiteSpace: "pre",
                                              lineBreak: "auto",
                                          }
                                        : undefined,
                            }}
                        />
                    </Box>
                    <AcxMessageBox variant="warning">
                        This update is immediate and may affect services using
                        this Data Source. Check with your admin if you&apos;re
                        unsure.
                    </AcxMessageBox>
                </Box>
            </AYSDialog.Content>
            <AYSDialog.Actions>
                <AcxButton
                    variant="outlined"
                    onClick={onClose}
                    disabled={isLoading}
                >
                    Cancel
                </AcxButton>
                <AcxButton
                    color="primary"
                    onClick={onConfirm}
                    loading={isLoading}
                >
                    Save Changes
                </AcxButton>
            </AYSDialog.Actions>
        </AYSDialog.Container>
    );
};

export default SaveConfigurationConfirmationDialog;
